import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShieldAlert, Lock, Search, Hash, Clock, User, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AuditLedgerEntry {
  id: string;
  timestamp: string;
  user: string;
  userId: string;
  role: string;
  action: string;
  resourceType: string;
  resourceId?: string;
  signature: string;
  previousHash: string;
  isSigned: boolean;
}

// Deterministic mock SHA-256 hash generator for demo
function mockSha256(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  return `sha256:${hex}${hex}${hex}${hex}${hex}${hex}${hex}${hex}`.slice(0, 71);
}

const DEMO_ENTRIES: AuditLedgerEntry[] = [
  {
    id: '1',
    timestamp: '2026-02-10T14:32:01Z',
    user: 'Dr. Larkin',
    userId: 'surgeon-001',
    role: 'surgeon',
    action: 'Surgeon Attestation — CPT 27447 confirmed as performed',
    resourceType: 'authorization_request',
    resourceId: 'CASE-2026-0147',
    signature: mockSha256('attestation-27447-larkin-1'),
    previousHash: mockSha256('genesis-block'),
    isSigned: true,
  },
  {
    id: '2',
    timestamp: '2026-02-10T14:28:44Z',
    user: 'Sarah Jensen',
    userId: 'ops-002',
    role: 'link_ops',
    action: 'Op Note uploaded and parsed — 3 CPTs detected',
    resourceType: 'case_document',
    resourceId: 'CASE-2026-0147',
    signature: mockSha256('opnote-upload-jensen-2'),
    previousHash: mockSha256('attestation-27447-larkin-1'),
    isSigned: true,
  },
  {
    id: '3',
    timestamp: '2026-02-10T13:55:12Z',
    user: 'System',
    userId: 'system',
    role: 'system',
    action: 'Modifier sequencing applied — 27447-LT, 29881-59',
    resourceType: 'modifier_application',
    resourceId: 'CASE-2026-0147',
    signature: mockSha256('modifier-seq-system-3'),
    previousHash: mockSha256('opnote-upload-jensen-2'),
    isSigned: true,
  },
  {
    id: '4',
    timestamp: '2026-02-10T12:10:33Z',
    user: 'Dr. Larkin',
    userId: 'surgeon-001',
    role: 'surgeon',
    action: 'Authorization Intent submitted — Total Knee Arthroplasty',
    resourceType: 'authorization_request',
    resourceId: 'CASE-2026-0147',
    signature: mockSha256('auth-intent-larkin-4'),
    previousHash: mockSha256('modifier-seq-system-3'),
    isSigned: true,
  },
  {
    id: '5',
    timestamp: '2026-02-10T11:45:00Z',
    user: 'Sarah Jensen',
    userId: 'ops-002',
    role: 'link_ops',
    action: 'Evidence bundle exported — PDF + FHIR payload',
    resourceType: 'export',
    resourceId: 'CASE-2026-0147',
    signature: mockSha256('export-jensen-5'),
    previousHash: mockSha256('auth-intent-larkin-4'),
    isSigned: true,
  },
];

const ROLE_COLORS: Record<string, string> = {
  surgeon: 'bg-primary/10 text-primary border-primary/20',
  link_ops: 'bg-chart-2/10 text-chart-2 border-chart-2/20',
  admin: 'bg-warning/10 text-warning border-warning/20',
  system: 'bg-muted text-muted-foreground border-border',
};

export function AuditLedger() {
  const [searchTerm, setSearchTerm] = useState('');
  const [integrityAlertOpen, setIntegrityAlertOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AuditLedgerEntry | null>(null);

  const filteredEntries = useMemo(() => {
    if (!searchTerm) return DEMO_ENTRIES;
    const lower = searchTerm.toLowerCase();
    return DEMO_ENTRIES.filter(
      (e) =>
        e.action.toLowerCase().includes(lower) ||
        e.user.toLowerCase().includes(lower) ||
        e.resourceId?.toLowerCase().includes(lower)
    );
  }, [searchTerm]);

  const handleAttemptEdit = (entry: AuditLedgerEntry) => {
    setSelectedEntry(entry);
    setIntegrityAlertOpen(true);
  };

  return (
    <>
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              Immutable Audit Ledger
            </CardTitle>
            <Badge variant="outline" className="text-[10px] font-mono border-primary/30 text-primary">
              {DEMO_ENTRIES.length} ENTRIES · CHAIN INTACT
            </Badge>
          </div>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search ledger entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-8 text-sm bg-secondary/50"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <div className="space-y-0">
              {filteredEntries.map((entry, index) => (
                <button
                  key={entry.id}
                  onClick={() => handleAttemptEdit(entry)}
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-border hover:bg-accent/50 transition-colors group',
                    index === 0 && 'bg-primary/5'
                  )}
                >
                  {/* Row 1: Timestamp + User + Role */}
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-[11px] font-mono text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                    <span className="text-[11px] text-muted-foreground">|</span>
                    <User className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-[11px] font-medium text-foreground">{entry.user}</span>
                    <Badge
                      variant="outline"
                      className={cn('text-[9px] py-0 px-1.5 h-4', ROLE_COLORS[entry.role])}
                    >
                      {entry.role.toUpperCase()}
                    </Badge>
                  </div>

                  {/* Row 2: Action */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <Activity className="w-3 h-3 text-primary shrink-0" />
                    <span className="text-xs text-foreground">{entry.action}</span>
                  </div>

                  {/* Row 3: Hash chain */}
                  <div className="flex items-center gap-2">
                    <Hash className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[280px]">
                      {entry.signature}
                    </span>
                    {entry.isSigned && (
                      <Lock className="w-3 h-3 text-success shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Integrity Alert Modal */}
      <Dialog open={integrityAlertOpen} onOpenChange={setIntegrityAlertOpen}>
        <DialogContent className="border-destructive/30 bg-card">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                <ShieldAlert className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <DialogTitle className="text-destructive">Integrity Alert</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Immutable Chain Violation Attempted
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-sm text-foreground leading-relaxed">
              <strong>This action is forbidden.</strong> All signed clinical documents
              are part of the immutable chain. Editing, deleting, or altering any
              entry would break the cryptographic hash sequence and violate HIPAA
              §164.312(c)(1) — Integrity Controls.
            </p>

            {selectedEntry && (
              <div className="rounded-md border border-border bg-secondary/50 p-3 space-y-1">
                <p className="text-[11px] text-muted-foreground">Protected Entry:</p>
                <p className="text-xs font-medium text-foreground">
                  {selectedEntry.action}
                </p>
                <p className="text-[10px] font-mono text-muted-foreground">
                  Signature: {selectedEntry.signature}
                </p>
              </div>
            )}

            <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
              <p className="text-xs text-warning">
                ⚠ This attempt has been logged to the Security Audit Trail per
                organizational policy.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIntegrityAlertOpen(false)}
            >
              Acknowledged
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
